import { useMemo, useState } from 'react'

/* A few real endpoints, with the shapes they actually return.
 *
 * The API page's whole claim is that the specification is public and you can
 * evaluate it before speaking to anyone. A page making that claim and then
 * showing nothing is asking to be taken on trust — the one thing the claim is
 * supposed to remove.
 *
 * EVERY PATH, FIELD AND STATUS BELOW IS REAL, read off the routing table and
 * the serializers rather than invented:
 *   - GET /api/v1/releases → Distribution::Serializers::Release.summary
 *   - POST /api/v1/payout_requests → PayoutRequestsController#serialize
 *   - GET /api/v1/statements/analytics, POST /api/v1/auth/session
 *
 * A fabricated field here would be worse than no example at all: the reader
 * can go and check, which is the point of publishing the specification, and
 * the first thing they would find is that we made it up.
 *
 * NO VALIDATE ENDPOINT HERE, DELIBERATELY. An earlier version showed
 * POST /releases/{id}/validate returning findings with severities — which
 * blurred two different things. The builder's deterministic pre-submit checks
 * are shipped; the agentic QC that reviews a release and asks the uploader
 * questions is NOT, and is marked "in build" on its own page. Showing a
 * findings payload here read as the second one being available to integrate
 * against today. Put it back when the engine ships, not before.
 */

interface Endpoint {
  key: string
  method: 'GET' | 'POST'
  path: string
  summary: string
  /** Why this endpoint is worth looking at, beyond what it returns. */
  note: string
  status: string
  response: string
}

const ENDPOINTS: Endpoint[] = [
  {
    key: 'releases',
    method: 'GET',
    path: '/api/v1/releases',
    summary: 'List the catalogue',
    note: 'Paginated, filterable by status. `editable` is computed for the caller rather than left for you to re-derive from `status` — the rule differs between an artist and a console, and duplicating it client-side is how the two drift.',
    status: '200 OK',
    response: `[
  {
    "id": 4471,
    "title": "Slow Monsoon",
    "release_type": "ep",
    "status": "live",
    "editable": false,
    "upc": "0198004417713",
    "external_id": "NW-2026-0114",
    "artwork_url": "https://…/artwork/4471.jpg",
    "created_at": "2026-01-14T09:22:41Z",
    "submitted_at": "2026-02-02T11:04:08Z",
    "went_live_at": "2026-03-14T00:00:00Z"
  }
]`,
  },
  {
    key: 'payout',
    method: 'POST',
    path: '/api/v1/payout_requests',
    summary: 'Request the available balance',
    note: 'Takes no body — the amount is whatever has settled, computed server-side, because a client-supplied figure is a number somebody can argue with. It opens a support ticket at the same time and hands back its reference, so the request and the conversation about it are one thing.',
    status: '201 Created',
    response: `{
  "id": 3390,
  "amount_usd": 312.44,
  "status": "requested",
  "note": null,
  "requested_at": "2026-09-01T08:14:02Z",
  "settled_at": null,
  "ticket_reference_code": "WB-TKT-8QK2"
}`,
  },
  {
    key: 'analytics',
    method: 'GET',
    path: '/api/v1/statements/analytics',
    summary: 'Revenue and units by dimension',
    note: 'The same aggregation the dashboard renders, so a figure in your own reporting and a figure an artist sees cannot disagree.',
    status: '200 OK',
    response: `{
  "period": "2026-08",
  "currency": "USD",
  "totals": { "revenue": 4180.62, "units": 533314 },
  "by_service": [
    { "name": "Spotify",     "revenue": 1922.40, "units": 207700 },
    { "name": "Apple Music", "revenue": 1108.85, "units": 54100 }
  ],
  "by_territory": [
    { "code": "IN", "revenue": 1630.11, "units": 249800 },
    { "code": "US", "revenue": 1012.44, "units": 96300 }
  ]
}`,
  },
  {
    key: 'session',
    method: 'POST',
    path: '/api/v1/auth/session',
    summary: 'Start an end-user session',
    note: 'End-user tokens are separate from your machine keys, and switching machine access off for an account never touches them — your artists keep signing in.',
    status: '201 Created',
    response: `{
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6…",
  "refresh_token": "b7f3c2a1-…",
  "expires_in": 900,
  "end_user": {
    "id": 8812,
    "name": "Mira Solace",
    "email": "mira@northwave.com"
  }
}`,
  },
]

const METHOD_TONE = {
  GET: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
  POST: 'border-sky-400/30 bg-sky-400/10 text-sky-300',
} as const

/* Deliberately tiny: enough to colour a JSON sample without pulling a syntax
   highlighter into the marketing bundle for four code blocks. Keys, strings,
   numbers and literals — anything else stays default. */
function highlight(json: string) {
  const tokens = json.split(/("(?:\\.|[^"\\])*"\s*:|"(?:\\.|[^"\\])*"|\b-?\d+\.?\d*\b|\btrue\b|\bfalse\b|\bnull\b)/g)
  return tokens.map((token, i) => {
    if (!token) return null
    if (/^"/.test(token) && /:\s*$/.test(token)) {
      return (
        <span key={i} className="text-sky-300/85">
          {token}
        </span>
      )
    }
    if (/^"/.test(token)) {
      return (
        <span key={i} className="text-emerald-300/80">
          {token}
        </span>
      )
    }
    if (/^-?\d/.test(token)) {
      return (
        <span key={i} className="text-amber-300/80">
          {token}
        </span>
      )
    }
    if (/^(true|false|null)$/.test(token)) {
      return (
        <span key={i} className="text-violet-300/85">
          {token}
        </span>
      )
    }
    return <span key={i}>{token}</span>
  })
}

export default function ApiExplorer() {
  const [active, setActive] = useState<Endpoint>(ENDPOINTS[0])

  // The payload is split on a capturing group and mapped into ~150 spans.
  // Only `active` can change it, so tokenizing in the render body redid the
  // whole thing on every render for an identical result.
  const highlighted = useMemo(() => highlight(active.response), [active])

  return (
    <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-5 sm:p-8">
      <div className="mb-6 text-center">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-[#ff8c4d]">
          Four of them, for a start
        </p>
        <h3
          className="mx-auto mt-2 max-w-xl text-lg font-semibold leading-snug text-white sm:text-xl"
          style={{ fontFamily: 'var(--wavbee-font-display)' }}
        >
          Real endpoints, and the shapes they actually return.
        </h3>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)]">
        {/* Endpoint list. */}
        <div role="group" aria-label="Choose an endpoint" className="flex flex-col gap-2">
          {ENDPOINTS.map((endpoint) => {
            const current = endpoint.key === active.key
            return (
              <button
                key={endpoint.key}
                type="button"
                aria-pressed={current}
                onClick={() => setActive(endpoint)}
                className={`rounded-xl border p-3 text-left transition-all ${
                  current
                    ? 'border-[#ff6a1a]/40 bg-[#ff6a1a]/[0.07]'
                    : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.18]'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`rounded border px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${METHOD_TONE[endpoint.method]}`}
                  >
                    {endpoint.method}
                  </span>
                  <span className="truncate font-mono text-[11px] text-white/70">
                    {endpoint.path}
                  </span>
                </span>
                <span className="mt-1.5 block text-[12px] text-white/50">{endpoint.summary}</span>
              </button>
            )
          })}
        </div>

        {/* Request and response. */}
        <div className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#08080a]">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.07] px-4 py-2.5">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className={`rounded border px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${METHOD_TONE[active.method]}`}
              >
                {active.method}
              </span>
              <span className="truncate font-mono text-[11px] text-white/70">{active.path}</span>
            </span>
            <span className="shrink-0 font-mono text-[10px] text-emerald-300/80">
              {active.status}
            </span>
          </div>

          <div className="border-b border-white/[0.07] px-4 py-3">
            <p className="text-[11px] leading-relaxed text-white/50">{active.note}</p>
          </div>

          {/* aria-live so a screen reader hears the payload change rather than
              only sighted users seeing it. */}
          <pre
            aria-live="polite"
            className="max-h-[19rem] overflow-auto px-4 py-3 font-mono text-[11px] leading-[1.7] text-white/75"
          >
            <code>{highlighted}</code>
          </pre>
        </div>
      </div>

      <p className="mx-auto mt-5 max-w-2xl text-center text-[0.75rem] leading-relaxed text-white/45">
        Every path, field and status above is the real one. The full reference is an OpenAPI
        document the build refuses to let drift — an endpoint added without documentation fails the
        test suite.
      </p>
    </div>
  )
}
